import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Alert,
  Autocomplete,
  Badge,
  Box,
  CircularProgress,
  CssBaseline,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Inventory2 as SupplyIcon,
  Assessment as DashboardIcon,
  ReceiptLongOutlined as OrdersIcon,
  CategoryOutlined as MaterialsIcon,
  HelpOutline as HelpOutlineIcon,
  Notifications as NotificationsIcon,
  Language as LanguageIcon,
  Check as CheckIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../stores/auth.store';
import { useNotificationStore } from '../stores/notification.store';
import { useOrganizationStore } from '../stores/organization.store';
import { OrganizationType, type OrganizationSchema } from '../types/organization.type';
import { useAuthPermissions } from '../hooks/useAuthPermissions';
import { MARKET_NAV_CATALOG } from '../auth';
import oneSignalService from '../services/onesignal.service';
import OrgLogo from './organizations/OrgLogo';
import { getOrganizationLogoUrl } from '../utils/organization.helpers';
import { Sidebar } from './Sidebar';
import { SelfSetupGate } from './selfSetup/SelfSetupGate';
import {
  SelfSetupNavItem,
  SelfSetupSidebarEffect,
} from './selfSetup/SelfSetupNavItem';
import { SelfSetupTourProvider } from './selfSetup/SelfSetupTourProvider';
import { SetupCoachmark } from './selfSetup/SetupCoachmark';
import {
  HELP_GUIDE_APPEARANCE,
  HELP_GUIDE_CHANGE_LANGUAGE,
} from '../constants/knowledgeBaseTryIt';
import {
  clearHelpGuideActive,
  markHelpGuideActive,
} from '../utils/helpGuideSession';
import { APP_LANGUAGES, setAppLanguage, type AppLanguage } from '../i18n/app';
import { APP_FONT_FAMILY } from '../constants/fonts';

const drawerWidth = 280;
const collapsedDrawerWidth = 76;
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'market.sidebarCollapsed';
const ACCOUNT_MENU_TRIGGER = 'data-account-menu-trigger';

/** Sidebar is rendered twice (mobile + desktop); pick the on-screen profile button. */
function findVisibleAccountMenuTrigger(): HTMLElement | null {
  const nodes = document.querySelectorAll<HTMLElement>(`[${ACCOUNT_MENU_TRIGGER}]`);
  for (const node of nodes) {
    const rect = node.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      return node;
    }
  }
  return null;
}

const LANGUAGE_FONT_FAMILY = APP_FONT_FAMILY;

const NAV_LABEL_KEYS: Record<string, string> = {
  Supply: 'supply',
  Dashboard: 'dashboard',
  Orders: 'orders',
  Materials: 'materials',
};

const NAV_ICONS: Record<string, React.ReactNode> = {
  '/supply': <SupplyIcon />,
  '/dashboard': <DashboardIcon />,
  '/orders': <OrdersIcon />,
  '/materials': <MaterialsIcon />,
};

interface LayoutProps {
  onThemeToggle: () => void;
  children?: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ onThemeToggle, children }) => {
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, i18n } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true',
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);
  const [helpAccountGuide, setHelpAccountGuide] = useState<string | null>(null);
  /** Second step of change-language help: spotlight the language picker menu. */
  const [languageGuidePhase, setLanguageGuidePhase] = useState<'item' | 'picker'>(
    'item',
  );
  /** Wait for the language menu Popper to settle before showing the picker tip. */
  const [languagePickerSettled, setLanguagePickerSettled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, accessibleOrgIds, isPlatformAdmin: platformAdmin } = useAuthStore();
  const { getOrganizations, setSelectedOrganization, organizations, selectedOrganization, isLoading } =
    useOrganizationStore();
  const { filterMarketNavItems } = useAuthPermissions();
  const { unreadCount, fetchNotifications } = useNotificationStore();

  const effectiveDrawerWidth = sidebarCollapsed ? collapsedDrawerWidth : drawerWidth;

  const contentTransition = theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  });

  useEffect(() => {
    if (
      helpAccountGuide !== HELP_GUIDE_CHANGE_LANGUAGE ||
      languageGuidePhase !== 'picker' ||
      !languageMenuAnchor
    ) {
      setLanguagePickerSettled(false);
      return;
    }
    setLanguagePickerSettled(false);
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setLanguagePickerSettled(true));
    });
    return () => window.cancelAnimationFrame(id);
  }, [helpAccountGuide, languageGuidePhase, languageMenuAnchor]);
  useEffect(() => {
    const guide = new URLSearchParams(location.search).get('helpGuide');
    if (
      guide !== HELP_GUIDE_CHANGE_LANGUAGE &&
      guide !== HELP_GUIDE_APPEARANCE
    ) {
      return;
    }
    setHelpAccountGuide(guide);
    setLanguageGuidePhase('item');
    markHelpGuideActive(guide);
    navigate({ pathname: location.pathname, search: '' }, { replace: true });
    setSidebarCollapsed(false);
    if (isMobileViewport) {
      setMobileOpen(true);
    }
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const button = findVisibleAccountMenuTrigger();
        if (button) setAnchorEl(button);
      });
    });
  }, [location.search, location.pathname, navigate, isMobileViewport]);

  const finishAccountGuide = () => {
    setHelpAccountGuide(null);
    setLanguageGuidePhase('item');
    setAnchorEl(null);
    setLanguageMenuAnchor(null);
    clearHelpGuideActive();
  };

  const showLanguageItemTip =
    helpAccountGuide === HELP_GUIDE_CHANGE_LANGUAGE &&
    Boolean(anchorEl) &&
    languageGuidePhase === 'item' &&
    !languageMenuAnchor;

  const showLanguagePickerTip =
    helpAccountGuide === HELP_GUIDE_CHANGE_LANGUAGE &&
    Boolean(languageMenuAnchor) &&
    languageGuidePhase === 'picker' &&
    languagePickerSettled;

  const recyclerOrganizations = useMemo(() => {
    let list = organizations.filter((o) => o.organization_type === OrganizationType.RECYCLER);
    if (!platformAdmin && accessibleOrgIds.length > 0) {
      const allowed = new Set(accessibleOrgIds);
      list = list.filter((o) => allowed.has(o.id));
    }
    return list;
  }, [organizations, accessibleOrgIds, platformAdmin]);

  const visibleNavItems = useMemo(() => {
    if (!selectedOrganization) {
      return [];
    }
    return filterMarketNavItems(MARKET_NAV_CATALOG, selectedOrganization.organization_type);
  }, [selectedOrganization, filterMarketNavItems]);

  const hasRecyclerOrgs = recyclerOrganizations.length > 0;
  const selectedIsRecycler = selectedOrganization?.organization_type === OrganizationType.RECYCLER;
  const showMainChrome = hasRecyclerOrgs && selectedIsRecycler;
  const isKnowledgeBaseRoute = location.pathname.startsWith('/knowledge-base');

  const orgLogoUrl = getOrganizationLogoUrl(selectedOrganization);

  const logoSrc = new URL(
    theme.palette.mode === 'dark' ? '/images/market_dark.png' : '/images/market_light.png',
    import.meta.url,
  ).href;

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const handleSidebarCollapseToggle = useCallback(
    () => setSidebarCollapsed((prev) => !prev),
    [],
  );

  const navLabel = (text: string) => t(`nav.${NAV_LABEL_KEYS[text] ?? text.toLowerCase()}`, text);
  const navIconFor = (path: string) => NAV_ICONS[path] ?? <SupplyIcon />;

  useEffect(() => {
    const savedOrg = sessionStorage.getItem('selectedOrganization');
    if (savedOrg) {
      try {
        const parsedOrg = JSON.parse(savedOrg) as OrganizationSchema;
        if (
          platformAdmin ||
          accessibleOrgIds.length === 0 ||
          accessibleOrgIds.includes(parsedOrg.id)
        ) {
          setSelectedOrganization(parsedOrg);
        } else {
          sessionStorage.removeItem('selectedOrganization');
        }
      } catch {
        console.error('Error parsing saved organization');
      }
    }
  }, [accessibleOrgIds, platformAdmin, setSelectedOrganization]);

  const fetchOrganizations = useCallback(async () => {
    await getOrganizations({ page: 1, limit: 100, name: undefined });
  }, [getOrganizations]);

  useEffect(() => {
    void fetchOrganizations();
  }, [fetchOrganizations]);

  useEffect(() => {
    if (isLoading || !organizations.length) return;
    if (!hasRecyclerOrgs) return;

    const savedRaw = sessionStorage.getItem('selectedOrganization');
    let fromSession: OrganizationSchema | null = null;
    if (savedRaw) {
      try {
        fromSession = JSON.parse(savedRaw) as OrganizationSchema;
      } catch {
        fromSession = null;
      }
    }

    const validSaved =
      fromSession?.organization_type === OrganizationType.RECYCLER &&
      recyclerOrganizations.some((r) => r.id === fromSession!.id)
        ? recyclerOrganizations.find((r) => r.id === fromSession!.id) ?? null
        : null;

    const next = validSaved ?? recyclerOrganizations[0];
    const needsSync =
      selectedOrganization?.id !== next.id ||
      selectedOrganization?.updated_at !== next.updated_at ||
      getOrganizationLogoUrl(selectedOrganization) !== getOrganizationLogoUrl(next);
    if (needsSync) {
      setSelectedOrganization(next);
      sessionStorage.setItem('selectedOrganization', JSON.stringify(next));
    }
  }, [
    isLoading,
    organizations.length,
    hasRecyclerOrgs,
    recyclerOrganizations,
    selectedOrganization?.id,
    setSelectedOrganization,
  ]);

  useEffect(() => {
    const registerOneSignalForOrganization = async () => {
      if (selectedOrganization && user && !user.data?.onesignal_player_id) {
        try {
          if (oneSignalService.isReady()) {
            await oneSignalService.registerPlayerWithBackend(selectedOrganization.id, user.sub);
            await oneSignalService.setExternalUserId(user.sub);
          }
        } catch (error) {
          console.error('Error registering OneSignal player:', error);
        }
      }
    };

    const timer = setTimeout(() => {
      void registerOneSignalForOrganization();
    }, 2000);

    return () => clearTimeout(timer);
  }, [selectedOrganization, user]);

  useEffect(() => {
    if (!selectedOrganization?.id) return;

    void fetchNotifications(selectedOrganization.id, { limit: 20 });
    const interval = setInterval(() => {
      void fetchNotifications(selectedOrganization.id, { limit: 20 });
    }, 30000);
    return () => clearInterval(interval);
  }, [selectedOrganization?.id, fetchNotifications]);

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
    if (helpAccountGuide) {
      setHelpAccountGuide(null);
      clearHelpGuideActive();
    }
  };

  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (helpAccountGuide === HELP_GUIDE_CHANGE_LANGUAGE) {
      // Close the Languages item tip first, then open the picker on a settled
      // anchor so the menu + tip don't flash mid-transition.
      setLanguageGuidePhase('picker');
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          const el = document.getElementById('help-account-languages');
          if (el) setLanguageMenuAnchor(el);
        });
      });
      return;
    }
    setLanguageMenuAnchor(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
    if (helpAccountGuide === HELP_GUIDE_CHANGE_LANGUAGE) {
      finishAccountGuide();
    }
  };

  const handleLanguageSelect = (language: AppLanguage) => {
    setAppLanguage(language);
    setLanguageMenuAnchor(null);
    setAnchorEl(null);
    if (helpAccountGuide === HELP_GUIDE_CHANGE_LANGUAGE) {
      finishAccountGuide();
    }
  };

  const handleOrganizationChange = async (
    _: unknown,
    newValue: OrganizationSchema | null,
  ) => {
    if (!newValue) return;

    setSelectedOrganization(newValue);
    sessionStorage.setItem('selectedOrganization', JSON.stringify(newValue));

    if (user && oneSignalService.isReady()) {
      try {
        await oneSignalService.registerPlayerWithBackend(newValue.id, user.sub);
        await oneSignalService.setExternalUserId(user.sub);
      } catch (error) {
        console.error('Error registering OneSignal for organization:', error);
      }
    }

    window.location.reload();
  };

  const mainContent = children ?? (
    isLoading && !organizations.length ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    ) : !hasRecyclerOrgs ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Alert severity="warning" sx={{ maxWidth: 480 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('gate.title')}
          </Typography>
          <Typography variant="body2">{t('gate.noRecyclerOrgs')}</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('gate.body')}
          </Typography>
        </Alert>
      </Box>
    ) : !selectedIsRecycler ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Alert severity="warning" sx={{ maxWidth: 480 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('gate.title')}
          </Typography>
          <Typography variant="body2">{t('gate.body')}</Typography>
        </Alert>
      </Box>
    ) : (
      <Outlet />
    )
  );

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Sidebar.Header
        collapsedChildren={
          <Box
            aria-label={t('app.name')}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="img"
                  src={logoSrc}
                  alt={t('app.name')}
                  sx={{
                    height: '28.75px',
                    width: 'auto',
                    maxWidth: 'none',
                    display: 'block',
                    ml: '-0.41px',
                    mt: '-0.13px',
                  }}
                />
              </Box>
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                fontSize: '0.66rem',
                letterSpacing: '0.06em',
                lineHeight: 1,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Market
            </Typography>
          </Box>
        }
      >
        <img
          src={logoSrc}
          alt={t('app.name')}
          style={{ height: 44, maxWidth: '100%', objectFit: 'contain' }}
        />
      </Sidebar.Header>

      {(showMainChrome || isKnowledgeBaseRoute) && hasRecyclerOrgs && (
        <Sidebar.ExpandedOnly>
          <Divider />
          <Box sx={{ px: 2, py: 2 }}>
            <Autocomplete
              value={selectedOrganization}
              onChange={handleOrganizationChange}
              options={recyclerOrganizations}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('common.organization')}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: `${theme.palette.primary.main}40` },
                      '&:hover fieldset': { borderColor: `${theme.palette.primary.main}80` },
                      '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
                    },
                    '& .MuiInputLabel-root': { color: `${theme.palette.primary.main}80` },
                    '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  }}
                />
              )}
              fullWidth
            />
          </Box>
          <Divider />
        </Sidebar.ExpandedOnly>
      )}

      {showMainChrome && (
        <Sidebar.Nav>
          <Sidebar.NavGroup label={t('nav.groups.overview', 'Overview')} defaultOpen>
            {visibleNavItems.map((item) => (
              <SelfSetupNavItem
                key={item.path}
                item={{
                  path: item.path,
                  text: item.text,
                  icon: navIconFor(item.path),
                  label: navLabel(item.text),
                  selected: location.pathname === item.path,
                }}
                onNavigate={(path) => navigate(path)}
              />
            ))}
          </Sidebar.NavGroup>
        </Sidebar.Nav>
      )}

      <Box sx={{ flexGrow: 1 }} />

      <Sidebar.ExpandedOnly>
        <Divider />
      </Sidebar.ExpandedOnly>
      <Sidebar.Nav>
        {selectedOrganization && (
          <Sidebar.NavItem
            icon={
              <Badge
                color="error"
                overlap="circular"
                showZero={false}
                badgeContent={unreadCount > 0 ? unreadCount : undefined}
              >
                <NotificationsIcon />
              </Badge>
            }
            label={t('nav.notifications')}
            selected={location.pathname === '/notifications'}
            onClick={() => navigate('/notifications')}
          />
        )}
        <Sidebar.NavItem
          icon={<HelpOutlineIcon />}
          label={t('nav.help')}
          selected={location.pathname.startsWith('/knowledge-base')}
          onClick={() => navigate('/knowledge-base')}
        />
        <Sidebar.NavItem
          icon={
            <OrgLogo
              name={selectedOrganization?.name ?? user?.username ?? t('account.title')}
              logoUrl={orgLogoUrl}
              size={28}
            />
          }
          label={user?.username ?? t('account.title')}
          selected={Boolean(anchorEl)}
          onClick={handleProfileMenuOpen}
          buttonProps={{ [ACCOUNT_MENU_TRIGGER]: 'true' }}
        />
      </Sidebar.Nav>

      <Sidebar.CollapseToggle
        onClick={handleSidebarCollapseToggle}
        expandLabel={t('nav.expandMenu')}
        collapseLabel={t('nav.collapseMenu')}
      />
    </Box>
  );

  return (
    <SelfSetupTourProvider>
    <SelfSetupSidebarEffect
      onExpandDesktop={() => setSidebarCollapsed(false)}
      onOpenMobile={() => {
        if (typeof window !== 'undefined' && window.matchMedia('(max-width: 599.95px)').matches) {
          setMobileOpen(true);
        }
      }}
    />
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          display: { xs: 'block', sm: 'none' },
          width: { sm: `calc(100% - ${effectiveDrawerWidth}px)` },
          ml: { sm: `${effectiveDrawerWidth}px` },
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: 'none',
          transition: { sm: contentTransition },
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <IconButton
            color="inherit"
            aria-label={t('nav.openMenu')}
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, color: theme.palette.primary.main }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
        </Toolbar>
      </AppBar>

      <Sidebar
        width={drawerWidth}
        collapsedWidth={collapsedDrawerWidth}
        collapsed={sidebarCollapsed}
        mobileOpen={mobileOpen}
        onMobileClose={handleDrawerToggle}
      >
        {drawer}
      </Sidebar>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${effectiveDrawerWidth}px)` },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: theme.palette.background.paper,
          transition: { sm: contentTransition },
        }}
      >
        <Toolbar sx={{ display: { xs: 'flex', sm: 'none' } }} />
        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            bgcolor: theme.palette.background.default,
            borderRadius: '16px',
            mt: { xs: 1.5, sm: 2 },
            mr: { xs: 1.5, sm: 2 },
            mb: { xs: 1.5, sm: 2 },
            ml: { xs: 1.5, sm: 0 },
          }}
        >
          {mainContent}
          <SelfSetupGate />
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            boxShadow: theme.shadows[3],
            borderRadius: '12px',
            minWidth: 220,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <OrgLogo
            name={selectedOrganization?.name ?? user?.username ?? t('account.title')}
            logoUrl={orgLogoUrl}
            size={36}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
              {user?.username}
            </Typography>
            {selectedOrganization && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {selectedOrganization.name}
              </Typography>
            )}
          </Box>
        </Box>
        <Divider />
        <SetupCoachmark
          open={helpAccountGuide === HELP_GUIDE_APPEARANCE && Boolean(anchorEl)}
          title={t('knowledgeBase.appearanceItemTitle')}
          subtitle={t('knowledgeBase.appearanceItemSubtitle')}
          onDismiss={finishAccountGuide}
          onNext={finishAccountGuide}
          nextLabel={t('selfSetup.tourDone')}
          fullWidth
          flat
          placement="left"
        >
          <MenuItem
            id="help-account-appearance"
            onClick={() => {
              onThemeToggle();
              if (helpAccountGuide === HELP_GUIDE_APPEARANCE) {
                finishAccountGuide();
              }
            }}
            sx={{
              transition: 'all 0.2s ease-in-out',
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: `${theme.palette.primary.main}10` },
            }}
          >
            <ListItemIcon>
              {theme.palette.mode === 'dark' ? (
                <DarkModeIcon fontSize="small" />
              ) : (
                <LightModeIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText primary={t('account.darkMode')} />
            <Switch
              edge="end"
              size="small"
              checked={theme.palette.mode === 'dark'}
              onClick={(event) => event.stopPropagation()}
              onChange={() => {
                onThemeToggle();
                if (helpAccountGuide === HELP_GUIDE_APPEARANCE) {
                  finishAccountGuide();
                }
              }}
            />
          </MenuItem>
        </SetupCoachmark>
        <SetupCoachmark
          open={showLanguageItemTip}
          title={t('knowledgeBase.changeLanguageItemTitle')}
          subtitle={t('knowledgeBase.changeLanguageItemSubtitle')}
          onDismiss={finishAccountGuide}
          fullWidth
          flat
          placement="left"
        >
          <MenuItem
            id="help-account-languages"
            onClick={handleLanguageMenuOpen}
            sx={{
              transition: 'all 0.2s ease-in-out',
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: `${theme.palette.primary.main}10` },
            }}
          >
            <ListItemIcon>
              <LanguageIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('account.languages')} />
            <ChevronRightIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
          </MenuItem>
        </SetupCoachmark>
        <Divider />
        <MenuItem
          onClick={logout}
          sx={{
            transition: 'all 0.2s ease-in-out',
            '&:hover': { bgcolor: `${theme.palette.error.main}10` },
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
          </ListItemIcon>
          {t('account.logout')}
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={languageMenuAnchor}
        open={Boolean(languageMenuAnchor)}
        onClose={handleLanguageMenuClose}
        hideBackdrop={showLanguagePickerTip}
        transitionDuration={
          helpAccountGuide === HELP_GUIDE_CHANGE_LANGUAGE ? 0 : undefined
        }
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            boxShadow: theme.shadows[3],
            borderRadius: '12px',
            minWidth: 240,
            maxHeight: 420,
            py: 0.5,
            ...(showLanguagePickerTip
              ? { zIndex: (t) => t.zIndex.modal + 6 }
              : null),
          },
        }}
        MenuListProps={{ dense: true }}
      >
        <SetupCoachmark
          open={showLanguagePickerTip}
          title={t('knowledgeBase.changeLanguagePickerTitle')}
          subtitle={t('knowledgeBase.changeLanguagePickerSubtitle')}
          onDismiss={finishAccountGuide}
          onNext={finishAccountGuide}
          nextLabel={t('selfSetup.tourDone')}
          fullWidth
          flat
          placement="right"
          holeRadius={12}
        >
          <Box id="help-account-language-picker">
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                px: 2,
                py: 0.5,
                color: 'text.secondary',
                fontWeight: 700,
                letterSpacing: '0.08em',
              }}
            >
              {t('account.languages')}
            </Typography>
            <Divider sx={{ mb: 0.5 }} />
            {APP_LANGUAGES.map((language) => {
              const isActive = i18n.language.startsWith(language.code);
              const localizedName = t(
                `languages.${language.englishLabel.toLowerCase()}`,
                language.englishLabel,
              );
              const showSecondary = localizedName !== language.nativeLabel;
              return (
                <MenuItem
                  key={language.code}
                  selected={isActive}
                  onClick={() => handleLanguageSelect(language.code)}
                  dir="ltr"
                  sx={{
                    gap: 1.25,
                    minHeight: 48,
                    py: 0.75,
                    px: 1.25,
                    mx: 0.75,
                    borderRadius: '10px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': { bgcolor: `${theme.palette.primary.main}10` },
                    '&.Mui-selected': {
                      bgcolor: `${theme.palette.primary.main}14`,
                      '&:hover': { bgcolor: `${theme.palette.primary.main}1F` },
                    },
                  }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      width: 30,
                      height: 30,
                      flexShrink: 0,
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      letterSpacing: '0.02em',
                      bgcolor: isActive
                        ? theme.palette.primary.main
                        : theme.palette.action.hover,
                      color: isActive
                        ? theme.palette.primary.contrastText
                        : theme.palette.text.secondary,
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    {language.code.toUpperCase()}
                  </Box>
                  <ListItemText
                    primary={language.nativeLabel}
                    secondary={showSecondary ? localizedName : undefined}
                    primaryTypographyProps={{
                      sx: {
                        fontFamily: LANGUAGE_FONT_FAMILY,
                        fontSize: '0.95rem',
                        fontWeight: isActive ? 700 : 500,
                        lineHeight: 1.35,
                        textAlign: 'start',
                      },
                      dir: 'ltr',
                    }}
                    secondaryTypographyProps={{
                      sx: {
                        fontFamily: LANGUAGE_FONT_FAMILY,
                        fontSize: '0.72rem',
                        lineHeight: 1.3,
                        textAlign: 'start',
                      },
                      dir: 'ltr',
                    }}
                  />
                  {isActive && (
                    <CheckIcon
                      fontSize="small"
                      sx={{ color: theme.palette.primary.main }}
                    />
                  )}
                </MenuItem>
              );
            })}
          </Box>
        </SetupCoachmark>
      </Menu>
    </Box>
    </SelfSetupTourProvider>
  );
};

export default Layout;
